import React, { useEffect, useState } from 'react';
import type { RichTextElement, SharedDataQuerySpec, SharedDataVariant } from 'coop-adventure-engine';
import apiService from '../../services/api';
import useAuthStore from '../../stores/authStore';
import guestService from '../../services/guestService';
import LoadingSpinner from './LoadingSpinner';
import {
  fetchSharedVariablesWithDedup,
  type SharedVariablesQueryResults
} from '../../utils/sharedVariablesQueryCoordinator';

function collectRichTextStrings(elements: RichTextElement[]): string[] {
  const strings: string[] = [];
  const walk = (items: RichTextElement[]) => {
    for (const item of items) {
      if (item.type === 'text' && typeof item.content === 'string') {
        strings.push(item.content);
      } else if (typeof item.content === 'string' && item.type !== 'text') {
        strings.push(item.content);
      }
      if (Array.isArray(item.content)) {
        walk(item.content as RichTextElement[]);
      }
      if (item.type === 'conditional') {
        if (Array.isArray(item.children)) {
          walk(item.children);
        }
        if (Array.isArray(item.elseChildren)) {
          walk(item.elseChildren);
        }
      }
      if (item.type === 'script' && Array.isArray(item.content)) {
        walk(item.content as RichTextElement[]);
      }
    }
  };
  walk(elements);
  return strings;
}

export function sharedDataContentIsVisible(content: RichTextElement[] | undefined): boolean {
  if (!content || content.length === 0) {
    return false;
  }
  return collectRichTextStrings(content).some((value) => value.trim().length > 0);
}

export type SharedDataFetchFn = (
  sessionId: string,
  queries: SharedDataQuerySpec[],
  content?: RichTextElement[]
) => Promise<SharedVariablesQueryResults>;

export interface SharedDataProps {
  sessionId: string;
  queries: SharedDataQuerySpec[];
  contentElements: RichTextElement[];
  className?: string;
  /** `card` (default boxed panel) or `plain` (inline with scene text). */
  variant?: SharedDataVariant;
  children: (data: SharedVariablesQueryResults) => React.ReactNode;
  /** Injectable for tests; defaults to backend batch query API. */
  fetchQuery?: SharedDataFetchFn;
}

type SharedDataState =
  | { status: 'loading' }
  | { status: 'ready'; data: SharedVariablesQueryResults }
  | { status: 'error'; message: string };

const defaultFetchQuery: SharedDataFetchFn = (sessionId, queries, content) =>
  apiService.querySharedVariables(sessionId, queries, content);

const blockStyle: React.CSSProperties = {
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '0.75rem',
  padding: '0.75rem 1rem',
  backdropFilter: 'blur(12px)',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
};

const SharedData: React.FC<SharedDataProps> = ({
  sessionId,
  queries,
  contentElements,
  className = '',
  variant = 'card',
  children,
  fetchQuery = defaultFetchQuery,
}) => {
  const [state, setState] = useState<SharedDataState>({ status: 'loading' });
  const isPlain = variant === 'plain';

  const queriesKey = JSON.stringify(queries);
  const contentKey = JSON.stringify(contentElements);

  useEffect(() => {
    let cancelled = false;
    const parsedQueries = JSON.parse(queriesKey) as SharedDataQuerySpec[];
    const parsedContent = JSON.parse(contentKey) as RichTextElement[];

    if (!parsedQueries.length) {
      setState({
        status: 'error',
        message: 'Shared-Data block has no queries configured.',
      });
      return () => {
        cancelled = true;
      };
    }

    setState({ status: 'loading' });

    const runFetch = async () => {
      const authState = useAuthStore.getState();
      if (authState.isGuest || guestService.getGuestData()) {
        await useAuthStore.getState().ensureValidToken().catch(() => {});
      }
      return fetchQuery(sessionId, parsedQueries, parsedContent);
    };

    fetchSharedVariablesWithDedup(
      sessionId,
      parsedQueries,
      runFetch,
      parsedContent
    )
      .then((data) => {
        if (!cancelled) {
          setState({ status: 'ready', data });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        const message =
          error && typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string'
            ? (error as { message: string }).message
            : 'Failed to load shared data.';
        setState({ status: 'error', message });
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, queriesKey, contentKey, fetchQuery]);

  if (state.status === 'ready' && !sharedDataContentIsVisible(state.data.content)) {
    return null;
  }

  return (
    <div
      className={`rich-text-shared-data${isPlain ? ' rich-text-shared-data--plain' : ''} ${className}`.trim()}
      style={isPlain ? undefined : blockStyle}
      data-testid="shared-data"
      data-status={state.status}
      data-variant={variant}
    >
      {state.status === 'loading' && (
        <div
          className={
            isPlain
              ? 'rich-text-shared-data-loading flex items-center gap-2'
              : 'rich-text-shared-data-loading flex items-center justify-center gap-2 py-4'
          }
          data-testid="shared-data-loading"
        >
          <LoadingSpinner size="sm" color="white" />
          <span className="text-sm text-white/80">{isPlain ? 'Loading…' : 'Loading shared data…'}</span>
        </div>
      )}

      {state.status === 'error' && (
        <div
          className="rich-text-shared-data-error text-sm text-red-300 py-2"
          data-testid="shared-data-error"
          role="alert"
        >
          {state.message}
        </div>
      )}

      {state.status === 'ready' && (
        <div className="rich-text-shared-data-content" data-testid="shared-data-content">
          {children(state.data)}
        </div>
      )}
    </div>
  );
};

export default SharedData;
